import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import { FarmlandAddReq } from '@/api/growth/farmland/type';
import { useEffect, useState } from 'react';
import { GrowthCropListItem } from '@/api/growth/crop/type';
import { GrowthCultivarListItem } from '@/api/growth/cultivar/type';
import { formatDateString, parseDate } from '@utils/Utils';
import { api as cropApi } from '@/api/growth/crop/api';
import { api as cultivarApi } from '@/api/growth/cultivar/api';
import Tooltip from '@components/common/Tooltip';
import PlantingImageGallery from '@components/common/PlantingImageGallery/PlantingImageGallery';

interface PlantingMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: string | number) => void;
}

const PlantingMng = ({ formData, onInputChange }: PlantingMngProps) => {
    const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
    const [isImageGalleryOpen2, setIsImageGalleryOpen2] = useState(false);

    const generateNumberOptions = (
        min: number,
        max: number,
        step: number = 1,
        unit: string = '개',
        includeNone: boolean = true,
        recommendations?: Record<number, string>,
    ): SelectOption[] => {
        const options: SelectOption[] = [];

        if (includeNone) {
            options.push({ value: '-99', label: '선택안함', selected: true });
        }

        for (let i = min; i <= max; i += step) {
            const recommendation = recommendations?.[i];
            const label = recommendation
                ? `${i}${unit} (${recommendation})`
                : `${i}${unit}`;

            options.push({
                value: String(i),
                label: label,
            });
        }

        return options;
    };

    const ppopOptions = generateNumberOptions(1, 40, 1, '개', true, {
        30: '콩',
        5: '감자',
        25: '벼',
        6: '옥수수',
    });
    const plrsOptions = generateNumberOptions(10, 100, 5, 'cm', true, {
        70: '콩',
        75: '감자,옥수수',
        30: '벼',
    });
    const pldpOptions = generateNumberOptions(2, 15, 1, 'cm', true, {
        4: '콩',
        10: '감자',
        5: '벼,옥수수',
    });

    const pageOptions = generateNumberOptions(0, 90, 5, '일', true, {
        25: '벼',
    });

    const penvOptions = generateNumberOptions(5, 30, 1, '℃', true, {
        23: '벼',
        24: '벼',
        25: '벼',
    });
    const plphOptions = generateNumberOptions(1, 5, 1, '개', true, {
        3: '벼',
    });
    const sprlOptions = generateNumberOptions(1, 10, 1, 'cm', true, {
        2: '씨감자',
    });

    const plmeOptions = [
        { value: '-99', label: '선택안함', selected: true },
        {
            value: 'B',
            label: '[고랑파종] 고랑/이랑 조성 후 파종 / 감자, 참마, 고구마, 상추, 양배추, 당근, 무',
        },
        {
            value: 'S',
            label: '[건조종자직파] 건조 종자를 밭이나 논에 바로 파종 / 옥수수, 콩, 땅콩, 보리, 밀, 벼(건답직파), 수수',
        },
        {
            value: 'C',
            label: '[삽목] 가지 또는 줄기 절편을 이용해 번식 / 사탕수수, 고구마, 포도, 무화과, 허브류',
        },
        {
            value: 'H',
            label: '[수평삽목] 줄기를 땅에 눕혀 식재, 발근/발아 유도 / 카사바, 사탕수수, 얌(yam), 포도',
        },
        {
            value: 'I',
            label: '[경사삽목] 줄기를 45° 정도 기울여 심음 / 카사바, 사탕수수, 일부 관목류',
        },
        {
            value: 'N',
            label: '[육묘이식] 육묘장에서 키운 묘를 본포로 옮김 / 벼, 상추, 배추, 고추, 토마토, 오이, 수박',
        },
        {
            value: 'P',
            label: '[발아종자파종] 종자를 미리 발아시킨 뒤 파종 / 벼(담수기계이앙), 일부 채소류',
        },
        {
            value: 'R',
            label: '[재생재배] 최초 수확 후 자란 새순을 다시 재배 / 사탕수수, 벼(재생벼), 수수',
        },
        {
            value: 'T',
            label: '[이식재배] 육묘 후 정식하는 전형적인 이식재배 / 벼(이앙), 토마토, 고추, 배추, 양배추, 오이, 파프리카',
        },
        {
            value: 'V',
            label: '[수직삽목] 절단 줄기를 똑바로 세워 식재 / 카사바, 사탕수수, 일부 관목류',
        },
    ];

    const pldsOptions = [
        { value: '-99', label: '선택안함', selected: true },
        {
            value: 'R',
            label: '[조파] 일정 간격의 줄(행)로 파종 / 벼, 옥수수, 콩, 밀, 보리, 면화',
        },
        {
            value: 'H',
            label: '[점파] 일정 거리마다 여러 종자를 모아 파종 / 옥수수, 수수, 고구마, 땅콩, 호박, 멜론, 수박',
        },
        {
            value: 'U',
            label: '[산파] 포장 전체에 고르게 흩뿌림 / 벼(담수산파), 밀, 보리, 잔디, 목초, 녹비작물',
        },
    ];

    const [cropOptions, setCropOptions] = useState<GrowthCropListItem[]>([]);
    const [cultivarOptions, setCultivarOptions] = useState<
        GrowthCultivarListItem[]
    >([]);

    const handleCultivarChange = (value: string) => {
        onInputChange('guIdx', parseInt(value));
    };

    const getCropSelectOptions = (): SelectOption[] => {
        return cropOptions.map(crop => ({
            value: String(crop.gcIdx),
            label: crop.gcName,
        }));
    };

    const getCultivarSelectOptions = (): SelectOption[] => {
        return cultivarOptions.map(cultivar => ({
            value: String(cultivar.guIdx),
            label: cultivar.guName,
        }));
    };

    const handleCropChange = (value: string) => {
        const gcIdx = parseInt(value);
        onInputChange('gcIdx', gcIdx);
        onInputChange('guIdx', 0);

        if (value) {
            const selectedCrop = cropOptions.find(crop => crop.gcIdx === gcIdx);
            if (selectedCrop && selectedCrop.gcId) {
                fetchCultivarList(selectedCrop.gcId);
            }
        } else {
            setCultivarOptions([]);
        }
    };

    const fetchCropList = async () => {
        const response = await cropApi.growthCropList();
        setCropOptions(response.data);
    };

    const fetchCultivarList = async (gcId: string) => {
        const response = await cultivarApi.growthCultivarList(gcId);
        setCultivarOptions(response.data);
        if (response.data && response.data.length > 0) {
            onInputChange('guIdx', response.data[0].guIdx);
        }
    };

    useEffect(() => {
        fetchCropList();
    }, []);

    useEffect(() => {
        const selectedCrop = cropOptions.find(
            crop => crop.gcIdx === formData.gcIdx,
        );
        if (selectedCrop && selectedCrop.gcId) {
            fetchCultivarList(selectedCrop.gcId);
        }
    }, [cropOptions]);

    return (
        <>
            <div className="d-flex align-items-center mb-2 " id="plantingMng">
                <i className="ri-spam-2-fill text-info fs-17 me-1"></i>
                <span className="text-muted fs-13 fw-normal">
                    파종을 했을경우, 파종전에 작업한 농작업내용을 입력해주세요.
                </span>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        생육작물 :
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={getCropSelectOptions()}
                        value={formData.gcIdx ? String(formData.gcIdx) : ''}
                        onChange={handleCropChange}
                        placeholder="선택해주세요"
                        selectClassName="form-control"
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        품종코드 :
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={getCultivarSelectOptions()}
                        value={formData.guIdx ? String(formData.guIdx) : ''}
                        onChange={handleCultivarChange}
                        placeholder="선택해주세요"
                        disabled={!formData.gcIdx}
                        selectClassName="form-control"
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        파종일시 :
                    </label>
                </div>
                <div className="col-md-10">
                    <DatePicker
                        selectedDate={parseDate(formData.pdate || '')}
                        onChange={value =>
                            onInputChange('pdate', formatDateString(value))
                        }
                        placeholder="파종일을 선택해주세요"
                        maxDate={undefined}
                        showYearDropdown={false}
                        showMonthDropdown={false}
                        disabled={formData.growthFlag !== 1}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        파종밀도:
                        <Tooltip
                            content="(1m²)단위 면적당 심은 종자의 개수입니다. 작물별 기준 단위가 있으므로 농업 기준에 맞게 입력하세요."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={ppopOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.ppop?.toString()}
                        onChange={value => onInputChange('ppop', value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        파종방법:
                        <Tooltip
                            content="작물을 어떤 방식으로 심었는지 선택합니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <div className="input-group">
                        <div className="flex-grow-1">
                            <SelectInput
                                options={plmeOptions}
                                required={false}
                                disabled={formData.growthFlag !== 1}
                                value={formData.plme?.toString()}
                                onChange={value => onInputChange('plme', value)}
                            />
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() =>
                                setIsImageGalleryOpen(!isImageGalleryOpen)
                            }
                        >
                            <i
                                className={`ri-${isImageGalleryOpen ? 'subtract' : 'add'}-line me-1`}
                            ></i>
                            예시 {isImageGalleryOpen ? '숨김' : '보기'}
                        </button>
                    </div>
                </div>
            </div>

            <PlantingImageGallery isOpen={isImageGalleryOpen} type="plme" />

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        심는방법:
                        <Tooltip
                            content="종자를 어떤 방식으로 배치했는지 선택합니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <div className="input-group">
                        <div className="flex-grow-1">
                            <SelectInput
                                options={pldsOptions}
                                required={false}
                                disabled={formData.growthFlag !== 1}
                                value={formData.plds?.toString()}
                                onChange={value => onInputChange('plds', value)}
                            />
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() =>
                                setIsImageGalleryOpen2(!isImageGalleryOpen2)
                            }
                        >
                            <i
                                className={`ri-${isImageGalleryOpen2 ? 'subtract' : 'add'}-line me-1`}
                            ></i>
                            예시 {isImageGalleryOpen2 ? '숨김' : '보기'}
                        </button>
                    </div>
                </div>
            </div>

            <PlantingImageGallery isOpen={isImageGalleryOpen2} type="plds" />

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        이랑간격:
                        <Tooltip
                            content="고랑과 고랑 사이의 거리입니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={plrsOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.plrs?.toString()}
                        onChange={value => onInputChange('plrs', value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        파종깊이:
                        <Tooltip
                            content="씨앗 또는 모종을 심은 깊이입니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={pldpOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.pldp?.toString()}
                        onChange={value => onInputChange('pldp', value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        모종나이:
                        <Tooltip
                            content="모종을 키운 날짜(묘령)입니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={pageOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.page?.toString()}
                        onChange={value => onInputChange('page', value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        육묘장온도:
                        <Tooltip
                            content="육묘·모종이 자란환경의 온도입니다.(하우스온도)"
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={penvOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.penv?.toString()}
                        onChange={value => onInputChange('penv', value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        구멍당종자수:
                        <Tooltip
                            content="점파 또는 포기 단위 재배 시,한 구멍(또는 포기)에 심는 개체 수입니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={plphOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.plph?.toString()}
                        onChange={value => onInputChange('plph', value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        새싹길이:
                        <Tooltip
                            content="심기 전 모종 또는 싹의 길이입니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </label>
                </div>
                <div className="col-md-10">
                    <SelectInput
                        options={sprlOptions}
                        required={false}
                        disabled={formData.growthFlag !== 1}
                        value={formData.sprl?.toString()}
                        onChange={value => onInputChange('sprl', value)}
                    />
                </div>
            </div>
        </>
    );
};

export default PlantingMng;
